import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Search, Download, Calendar, ChevronLeft, ChevronRight, FileText } from 'lucide-react';

const CHANGE_TYPES = ['CREATE', 'UPDATE', 'DELETE', 'TRANSFER', 'BULK_TRANSFER', 'BULK_UPLOAD', 'MIGRATION'];
const ENTITY_TYPES = ['Family', 'Member', 'Mohalla', 'Sector', 'FMB', 'Distribution', 'User', 'System'];

export default function AuditPage() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changeType, setChangeType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [todayCount, setTodayCount] = useState(0);

  const limit = 25;

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (changeType) params.append('change_type', changeType);
      if (entityType) params.append('entity_type', entityType);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await api.get(`/audit?${params}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [api, page, changeType, entityType, dateFrom, dateTo]);

  const fetchTodayChanges = useCallback(async () => {
    try {
      const response = await api.get('/audit/today');
      setTodayCount(response.data.logs.length);
    } catch (error) {
      console.error('Failed to fetch today changes');
    }
  }, [api]);

  useEffect(() => {
    fetchLogs();
    fetchTodayChanges();
  }, [fetchLogs, fetchTodayChanges]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await api.get(`/download/audit?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Audit_Logs.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export started');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const getChangeTypeBadge = (type) => {
    const colors = {
      'CREATE': 'bg-emerald-100 text-emerald-700',
      'UPDATE': 'bg-blue-100 text-blue-700',
      'DELETE': 'bg-red-100 text-red-700',
      'TRANSFER': 'bg-purple-100 text-purple-700',
      'BULK_TRANSFER': 'bg-indigo-100 text-indigo-700',
      'BULK_UPLOAD': 'bg-amber-100 text-amber-700'
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-800">Audit Logs</h1>
          <p className="text-sm text-slate-500">Track all changes made to the system</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Card className="border-0 shadow-sm bg-rose-50 px-4 py-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-rose-500" />
              <span className="text-sm text-rose-700 font-medium">Today: {todayCount} changes</span>
            </div>
          </Card>
          <Button variant="outline" onClick={handleExport} data-testid="export-audit-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Select value={changeType} onValueChange={(v) => { setChangeType(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="change-type-filter">
                <SelectValue placeholder="Change Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CHANGE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={entityType} onValueChange={(v) => { setEntityType(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="entity-type-filter">
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {ENTITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <div>
              <Input 
                type="date" 
                value={dateFrom} 
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                placeholder="From Date"
                data-testid="date-from-input"
              />
            </div>
            <div>
              <Input 
                type="date" 
                value={dateTo} 
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                placeholder="To Date"
                data-testid="date-to-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                <TableHead>Changed By</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Member/HOF</TableHead>
                <TableHead>Old Value</TableHead>
                <TableHead>New Value</TableHead>
                <TableHead>Mohalla</TableHead>
                <TableHead>Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleDateString('en-IN')}<br/>
                      <span className="text-slate-400">{new Date(log.timestamp).toLocaleTimeString('en-IN')}</span>
                    </TableCell>
                    <TableCell className="font-medium">{log.changed_by}</TableCell>
                    <TableCell>
                      <Badge className={getChangeTypeBadge(log.change_type)}>
                        {log.change_type}
                      </Badge>
                    </TableCell>
                    <TableCell>{log.entity_type}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {log.member_name && <div>{log.member_name}</div>}
                        {log.hof_name && <div className="text-slate-400 text-xs">HOF: {log.hof_name}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-32 truncate text-xs text-red-600">{log.old_value || '-'}</TableCell>
                    <TableCell className="max-w-32 truncate text-xs text-emerald-600">{log.new_value || '-'}</TableCell>
                    <TableCell className="text-xs">{log.mohalla || '-'}</TableCell>
                    <TableCell className="max-w-40 truncate text-xs text-slate-500">{log.remarks || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t">
          <p className="text-sm text-slate-500">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} logs
          </p>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="audit-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600">Page {page} of {totalPages || 1}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="audit-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
